import type { TypePolicies } from '@apollo/client';
import { ApolloProvider } from '@apollo/client';
import { useGetAccessToken, useSession } from '@tbitop/authentication';
import { isCapacitor } from '@tbitop/platform';
import { defaultTo } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { useEffect, useMemo } from 'react';
import type { GlobalGraphQLErrorHandler } from './global-graphql-error-handler';
import { GraphQLErrorHandlerContext } from './global-graphql-error-handler';
import { createGraphQLClient } from './graphql-client';

const defaultTypePolicies = {};

type GraphQLProviderProps = {
  id: string;
  uri: string;
  connectToDevTools?: boolean;
  typePolicies?: TypePolicies;
  errorHandler: GlobalGraphQLErrorHandler;
};

export const GraphQLProvider = ({ id, uri, connectToDevTools = false, typePolicies, errorHandler, children }: PropsWithChildren<GraphQLProviderProps>) => {
  const getAccessToken = useGetAccessToken();
  const session = useSession();

  const client = useMemo(() => createGraphQLClient(
    id,
    uri,
    connectToDevTools,
    defaultTo(typePolicies, defaultTypePolicies),
    getAccessToken,
  ), [id, connectToDevTools, getAccessToken, typePolicies, uri]);

  // on capacitor, logging out will not unload the page so we have to reset the store manually
  useEffect(() => {
    if (!isCapacitor()) {
      return;
    }

    return session?.onLogout(() => {
      setTimeout(() => client?.resetStore());
    });
  }, [client, session]);

  return (
    <GraphQLErrorHandlerContext.Provider value={errorHandler}>
      <ApolloProvider client={client}>
        {children}
      </ApolloProvider>
    </GraphQLErrorHandlerContext.Provider>
  );
};

